import React, { useEffect, useState } from "react";
import { Button, Col, Row, FormGroup, CustomInput } from "reactstrap";
import swal from "@sweetalert/with-react";
import axios from "axios";
import { Link } from "react-router-dom";
import moment from "moment";
import { baseurl } from "../../../../../baseurl";
import { checkPermission } from "../../../../../Helper";
import { GetIp } from "../../../ShortCutComponents/Get_ip";
import ResponseLoader from "../../../ShortCutComponents/ResponseLoader";
import { csvFileToArray } from "../../../../Features/Imports/data";
import productionNoteSheet from "../../../../Features/Imports/assets/productionNoteSheet.xlsx";

const MASTER_COLUMNS = new Set([
  "ProductionRef",
  "ProductionDate",
  "ProductionType",
  "Branch",
  "Remarks",
]);

const DETAIL_COLUMNS = new Set([
  "ItemCode",
  "ItemName",
  "Produce Qty",
  "Rate",
  "Amount",
  "Line Remarks",
]);

const SHEET_HEADERS = [
  "ProductionRef",
  "ProductionDate",
  "ProductionType",
  "Branch",
  "Remarks",
  "ItemCode",
  "ItemName",
  "Produce Qty",
  "Rate",
  "Amount",
  "Line Remarks",
];

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

const toNumber = (value) => {
  if (isBlank(value)) return 0;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundTo = (value, decimals) =>
  Number(Number(value).toFixed(decimals));

const toSqlDate = (value) => {
  if (isBlank(value)) return "";
  const parsed = moment(value, ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY"], true);
  const fallback = parsed.isValid() ? parsed : moment(value);
  return fallback.isValid() ? fallback.format("YYYY-MM-DD") : "";
};

export default function ProductionNoteZTExcelLoader(props) {
  var username = localStorage.getItem("username");
  var token = localStorage.getItem("token");
  var companyId = localStorage.getItem("companyId");
  var currencyId = localStorage.getItem("currencyId");

  const [loadingResponce, setLoadingResponce] = useState(false);
  const [percentageLoader, setPercentageLoader] = useState(false);
  const [goodRows, setGoodRows] = useState([]);
  const [badRows, setBadRows] = useState([]);
  const [showBadRows, setShowBadRows] = useState(true);
  const [file, setfile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [ipCountry, setIpCountry] = useState({ userIp: "", userCountry: "" });
  const [branchLov, setBranchLov] = useState({
    show: false,
    defaultId: "",
    label: "Branch",
  });

  useEffect(() => {
    const funCall = async (value) => {
      await asyncRollCheck(value);
      await getBranchConfiguration();
    };
    funCall(1);
    getIpCountry();
  }, []);

  const asyncRollCheck = async (value) => {
    let pathname = "/ProductionNoteZTExcelLoader";
    let status = value;

    let hasPermission = await checkPermission(pathname, status);
    if (hasPermission) {
      return true;
    }
  };

  const getUserIp = async function () {
    let res = await GetIp();
    return {
      userIp: res.ip,
      userCountry: res.country,
    };
  };

  const getIpCountry = async () => {
    let { userIp, userCountry } = await getUserIp();
    setIpCountry({ userIp, userCountry });
  };

  const getBranchConfiguration = async () => {
    try {
      const response = await axios.post(
        `${baseurl}/lovs/getGlPreferences`,
        { companyId, username },
        { headers: { authorization: token } },
      );
      const preferences = response.data.data[0] || {};
      const multiBranch =
        preferences.multi_branch_yn === 1 ||
        preferences.multi_branch_yn === "1";

      setBranchLov({
        show: multiBranch,
        defaultId: preferences.def_sub_inv_id || "",
        label: preferences.sub_inv_desc || "Branch",
      });
    } catch (error) {
      console.error("GL preferences could not be read:", error);
      swal({
        title: "Error",
        text: "Branch configuration could not be read, please refresh the page.",
        icon: "error",
      });
    }
  };

  const handleOnChange = (event) => {
    setfile(event.target.files[0]);
  };

  const handleOnChangeFile = async () => {
    if (!file) {
      swal({
        title: "File Not Selected!",
        text: "Please Select File",
        icon: "warning",
      });
      return;
    }

    setLoadingResponce(true);

    try {
      setFileName(file.name);

      const jsonData = await csvFileToArray(file);
      const rows = jsonData.filter((row) => row.length > 0);

      if (!rows.length) {
        throw new Error("The file contains no valid data");
      }

      const headers = rows[0].map((header) => String(header).trim());
      const missingColumns = SHEET_HEADERS.filter(
        (header) => !headers.includes(header),
      );

      if (missingColumns.length) {
        swal({
          title: "Columns Missing!",
          text: `Please fill the ${missingColumns[0]} column`,
          icon: "warning",
        });
        setLoadingResponce(false);
        return;
      }

      const indexOf = (column) => headers.indexOf(column);

      const notes = [];
      let currentNote = null;
      let lastRef = null;

      rows.slice(1).forEach((row) => {
        const ref = row[indexOf("ProductionRef")];
        const hasDetailValue =
          !isBlank(row[indexOf("ItemCode")]) ||
          !isBlank(row[indexOf("Produced")]) ||
          !isBlank(row[indexOf("Amount")]);

        const master = {};
        MASTER_COLUMNS.forEach((column) => {
          master[column] = row[indexOf(column)] ?? null;
        });

        const detail = {};
        DETAIL_COLUMNS.forEach((column) => {
          detail[column] = row[indexOf(column)] ?? null;
        });

        const isNewNote = !isBlank(ref) && String(ref) !== String(lastRef);

        if (isNewNote || !currentNote) {
          if (!currentNote && !isNewNote && !hasDetailValue) {
            return;
          }
          currentNote = { master: master, details: [] };
          notes.push(currentNote);
          if (!isBlank(ref)) lastRef = ref;
        }

        if (hasDetailValue) {
          currentNote.details.push(detail);
        }
      });

      const usableNotes = notes.filter((note) => note.details.length > 0);

      if (!usableNotes.length) {
        throw new Error("The file contains no valid detail data");
      }

      await verifyExcelData(usableNotes);
    } catch (error) {
      setLoadingResponce(false);
      setPercentageLoader(false);
      console.error("Error processing file:", error);
      swal({
        title: "Error",
        text: error.message || "Failed to process file",
        icon: "error",
      });
    }
  };

  const verifyExcelData = async (notes) => {
    setPercentageLoader(true);

    try {
      const { userIp, userCountry } = ipCountry;

      const response = await axios.post(
        `${baseurl}/production/verifyProductionNoteExcelData`,
        {
          companyId,
          excelData: notes,
          username: username,
          userIp,
          userCountry,
        },
        {
          headers: {
            authorization: token,
          },
        },
      );

      const responseData = response.data.data || [];
      const defaultBranchId = toNumber(branchLov.defaultId);
      const groupedGoodRows = [];
      const groupedBadRows = [];
      let goodLines = 0;
      let badLines = 0;

      notes.forEach((note, index) => {
        const verifiedNote = responseData[index] || {};
        const masterResult = verifiedNote.master?.result?.[0] || {};
        const verifiedDetails = verifiedNote.details || [];

        const prodTypeId = toNumber(masterResult.prod_type_id);
        const productionDate = toSqlDate(note.master.ProductionDate);
        const resolvedBranchId = toNumber(masterResult.sub_inv_id);
        const branchId = resolvedBranchId > 0 ? resolvedBranchId : defaultBranchId;

        const masterInvalid = {};
        if (!(prodTypeId > 0)) masterInvalid.ProductionType = true;
        if (!(branchId > 0)) masterInvalid.Branch = true;
        if (!productionDate) masterInvalid.ProductionDate = true;

        let detailsValid = true;

        const details = note.details.map((detail, detailIndex) => {
          const detailResult = verifiedDetails[detailIndex]?.result?.[0] || {};

          const itemId = toNumber(detailResult.item_id);
          const uomId = toNumber(detailResult.UOM_ID);
          const uomValid = toNumber(detailResult.uom_valid) === 1;
          const itemRate = toNumber(detailResult.ItemRate);

          const produced = roundTo(toNumber(detail["Produce Qty"]), 3);
          let rate = toNumber(detail.Rate);
          let amount = toNumber(detail.Amount);
          if (rate > 0) amount = roundTo(produced * rate, 2);

          const invalid = {};
          if (!(itemId > 0)) {
            invalid.ItemCode = "Item not found";
          } else if (!uomValid) {
            invalid.ItemCode = `Item ${detailResult.ITEM_CODE || detail.ItemCode} has no valid UOM`;
          }
          if (!(produced > 0)) invalid["Produce Qty"] = "Produced qty must be greater than zero";
          if (!(rate > 0)) invalid.Rate = "Rate is required and must be greater than zero";
          if (!(amount > 0)) invalid.Amount = "Amount must be greater than zero";

          const lineValid =
            itemId > 0 &&
            uomValid &&
            produced > 0 &&
            rate > 0 &&
            amount > 0 &&
            !!productionDate;

          if (!lineValid) detailsValid = false;

          return {
            ItemCode: detailResult.ITEM_CODE || detail.ItemCode || "",
            ItemName: detailResult.ITEM_DESC || detail.ItemName || "",
            UOM: detailResult.UOM_DESC || "",
            item_id: itemId,
            uom_id: uomId,
            Produced: produced,
            Rate: rate,
            Amount: amount,
            Remarks: detail["Line Remarks"] || "",
            prod_det_id: 0,
            invalid: invalid,
          };
        });

        const formattedRow = {
          master: {
            ProductionRef: note.master.ProductionRef || "",
            ProductionDate: productionDate || note.master.ProductionDate || "",
            ProductionType: note.master.ProductionType || "",
            Branch: true
              ? note.master.Branch || ""
              : "",
            Master_Branch_id: branchId,
            Prod_Type_id: prodTypeId,
            Remarks: note.master.Remarks || "",
            invalid: masterInvalid,
          },
          details: details,
        };

        const dateValid = !!productionDate;
        const masterValid = prodTypeId > 0 && branchId > 0 && dateValid;

        if (masterValid && detailsValid) {
          groupedGoodRows.push(formattedRow);
          goodLines += details.length;
        } else {
          groupedBadRows.push(formattedRow);
          badLines += details.length;
        }
      });

      setPercentageLoader(false);
      setLoadingResponce(false);
      setGoodRows(groupedGoodRows);
      setBadRows(groupedBadRows);
      setShowBadRows(groupedBadRows.length > 0);

      swal({
        title: "Done",
        text: `Verification completed. ${groupedGoodRows.length} Good Row(s), ${groupedBadRows.length} Bad Row(s).`,
        icon: groupedBadRows.length ? "warning" : "success",
      });
    } catch (error) {
      console.error("Error in verifyExcelData:", error);
      setPercentageLoader(false);
      setLoadingResponce(false);

      swal({
        title: "Error",
        text:
          error.response?.data?.message ||
          "Something went wrong, try to refresh the page and try again.",
        icon: "error",
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!goodRows.length) {
      swal({
        title: "Nothing To Save",
        text: "The sheet has no valid note to post.",
        icon: "warning",
      });
      return;
    }

    setPercentageLoader(true);
    setLoadingResponce(true);

    const { userIp, userCountry } = ipCountry;

    try {
      const response = await axios.post(
        `${baseurl}/production/addImportProductionNoteData`,
        {
          companyId,
          username: username,
          productionNotes: goodRows,
          fileName,
          conv_Rate: 1,
          currency_Id: currencyId,
          userIp,
          userCountry,
        },
        {
          headers: {
            authorization: token,
          },
        },
      );

      setPercentageLoader(false);
      setLoadingResponce(false);

      const { dublicate, message, data } = response.data;

      if (dublicate) {
        swal({
          title: "Warning",
          text: message,
          icon: "warning",
        });
        return;
      }

      if (message === "Item UOM Missing") {
        swal({
          title: `Item ${response.data.itemCode} has no valid UOM`,
          text: "The item has no UOM, or its UOM is missing from the UOM master. Fix it on the Item master and upload the sheet again - nothing was saved.",
          icon: "error",
        });
        return;
      }

      if (message === "Negavtive Item") {
        swal({
          title: `Item ${response.data.itemCode} goes negative`,
          text: `There is not enough stock on ${response.data.resDate} to post this note. Nothing was saved.`,
          icon: "error",
        });
        return;
      }

      if (message !== "success") {
        swal({
          title: "Warning",
          text: message || "The sheet could not be posted.",
          icon: "warning",
        });
        return;
      }

      await swal({
        title: "Saved Successfully!",
        text: `${data?.insertedCount ?? goodRows.length} production note(s) added.`,
        icon: "success",
      });

      props.history.push("/OverViewProduction");
    } catch (error) {
      console.error("Error saving production notes:", error);
      setPercentageLoader(false);
      setLoadingResponce(false);

      swal({
        title: "Warning",
        text:
          error.response?.data?.message ||
          "Something went wrong, try to refresh the page and try again.",
        icon: "warning",
      });
    }
  };

  const NotesTable = ({ data, title }) => {
    if (!data || !data.length) return null;

    const cellStyle = (message) => ({
      border: "1px solid black",
      backgroundColor: message ? "#FF0000" : "transparent",
      whiteSpace: "nowrap",
    });

    return (
      <div style={{ marginBottom: 30 }}>
        <h5>{title}</h5>
        <table className="table" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>Production Ref</th>
              <th>Date</th>
              <th>Production Type</th>
              <th>{branchLov.label}</th>
              <th>Remarks</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>UOM</th>
              <th>Produce Qty</th>
              <th>Rate</th>
              <th>Amount</th>
              <th>Line Remarks</th>
            </tr>
          </thead>
          <tbody>
            {data.map((note, noteIndex) =>
              note.details.map((detail, detailIndex) => (
                <tr key={`${noteIndex}-${detailIndex}`}>
                  {detailIndex === 0 && (
                    <>
                      <td rowSpan={note.details.length} style={cellStyle(false)}>
                        {note.master.ProductionRef}
                      </td>
                      <td
                        rowSpan={note.details.length}
                        style={cellStyle(note.master.invalid.ProductionDate)}
                      >
                        {note.master.ProductionDate}
                      </td>
                      <td
                        rowSpan={note.details.length}
                        style={cellStyle(note.master.invalid.ProductionType)}
                      >
                        {note.master.ProductionType}
                      </td>
                      <td
                        rowSpan={note.details.length}
                        style={cellStyle(note.master.invalid.Branch)}
                      >
                        {(note.master.Branch || "-")}
                      </td>
                      <td rowSpan={note.details.length} style={cellStyle(false)}>
                        {note.master.Remarks}
                      </td>
                    </>
                  )}
                  <td
                    style={cellStyle(detail.invalid.ItemCode)}
                    title={detail.invalid.ItemCode || ""}
                  >
                    {detail.ItemCode}
                  </td>
                  <td style={cellStyle(false)}>{detail.ItemName}</td>
                  <td style={cellStyle(detail.invalid.ItemCode)}>
                    {detail.UOM}
                  </td>
                  <td
                    style={cellStyle(detail.invalid["Produce Qty"])}
                    title={detail.invalid["Produce Qty"] || ""}
                  >
                    {detail.Produced}
                  </td>
                  <td
                    style={cellStyle(detail.invalid.Rate)}
                    title={detail.invalid.Rate || ""}
                  >
                    {detail.Rate}
                  </td>
                  <td
                    style={cellStyle(detail.invalid.Amount)}
                    title={detail.invalid.Amount || ""}
                  >
                    {detail.Amount}
                  </td>
                  <td style={cellStyle(false)}>{detail.Remarks}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {loadingResponce || percentageLoader ? (
        <ResponseLoader percentage={percentageLoader} />
      ) : (
        <div>
          <Row className="Creative-Invoice-header-styling">
            <Col xs="6" className="card-header-Creative-Invoice">
              <strong>
                <h4>Import Production Note Sheet</h4>
              </strong>
            </Col>
            <Col xs="6" className="card-header-Creative-Invoice">
              <div
                style={{
                  textAlign: "right",
                }}
              >
                <Link
                  to="/OverViewProduction"
                  style={{
                    fontFamily: "Times New Roman",
                    fontWeight: "normal",
                    margin: "10px",
                  }}
                >
                  <u className="Back-to-Overview">Back to Overview</u>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-chevron-left mx-2 icon-color"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
                    />
                  </svg>
                </Link>
              </div>
            </Col>
          </Row>

          <div className="mt-3">
            <div className="row">
              <div className="col-4 mt-1">
                <a href={productionNoteSheet} download>
                  Download example CSV
                </a>
              </div>
            </div>

            <FormGroup>
              <div className="row">
                <div className="col-11">
                  <CustomInput
                    type="file"
                    id="exampleCustomFileBrowser"
                    name="customFile"
                    accept=".csv,.xlsx"
                    required
                    onChange={handleOnChange}
                  />
                </div>
                <div className="col-1">
                  <span className="d-flex flex-row-reverse">
                    <Button
                      className="mr-1 btn-outline-primary"
                      onClick={handleOnChangeFile}
                    >
                      Import
                    </Button>
                  </span>
                </div>
              </div>

              <div className="row">
                <div className="col-3">
                  <span className="">
                    <Button
                      hidden={goodRows.length === 0 && badRows.length === 0}
                      className={`mr-1 mt-1 ${
                        showBadRows
                          ? "btn-outline-success"
                          : "btn-outline-danger"
                      }`}
                      onClick={() => setShowBadRows(!showBadRows)}
                    >
                      Show {showBadRows ? "Good" : "Bad"} Rows
                    </Button>
                  </span>
                </div>

                {(goodRows.length > 0 || badRows.length > 0) && (
                  <div className="col-6 mt-2">
                    <span style={{ color: "green" }}>
                      No of Good Rows : {goodRows.length}
                    </span>{" "}
                    |
                    <span className="ml-2" style={{ color: "red" }}>
                      No of Bad Rows : {badRows.length}
                    </span>
                    {true ? null : (
                      <span className="ml-3 text-muted">
                        {branchLov.label}:{" "}
                        {branchLov.defaultId
                          ? `default (${branchLov.defaultId})`
                          : "not configured"}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </FormGroup>

            <div style={{ overflowX: "auto" }}>
              {showBadRows ? (
                <NotesTable data={badRows} title="❌ Bad Rows" />
              ) : (
                <NotesTable data={goodRows} title="✅ Good Rows" />
              )}
            </div>

            <hr
              style={{
                border: "1px solid",
                marginBottom: "3vh",
                marginTop: "1vh",
                borderColor: "#E4E5E6",
              }}
            />

            <div className="col-md-12 d-flex flex-row-reverse">
              <FormGroup>
                <Button
                  onClick={handleSubmit}
                  className="mr-1 invoiceSubmitButton"
                  disabled={goodRows.length === 0}
                >
                  Save Changes
                </Button>
                <Link to="/OverViewProduction">
                  <Button className="mr-1" size="sm">
                    Cancel
                  </Button>
                </Link>
              </FormGroup>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

